// Guest (anonymous) mode: the pure decisions behind "try it without an
// account". Kept out of the React components so they're unit-testable
// (tests/anonMode.test.ts) — same pattern as authCallbackDecision.ts.

/** Shape-tolerant view of a Supabase User for the fields we care about. */
interface UserLike {
  is_anonymous?: boolean;
  email?: string | null;
  user_metadata?: Record<string, unknown> | null;
}

/** True only for Supabase's explicit anonymous flag — never inferred. */
export function isAnonymousUser(user: UserLike | null | undefined): boolean {
  return user?.is_anonymous === true;
}

/** Display-name prefill: OAuth metadata, then email local-part, else "" —
 *  guests have neither, so their field simply starts blank. */
export function defaultDisplayName(user: UserLike | null | undefined): string {
  if (!user) return "";
  const meta = user.user_metadata ?? {};
  const fromMeta = meta.full_name || meta.name || meta.user_name;
  if (typeof fromMeta === "string" && fromMeta.trim()) return fromMeta;
  return user.email?.split("@")[0] ?? "";
}

/** Human copy for the auth errors guests can hit while the Supabase dashboard
 *  toggles are still off — the buttons must fail helpfully, not cryptically. */
export function friendlyAuthError(err: unknown): string {
  const raw =
    err instanceof Error ? err.message : typeof err === "string" ? err : "";
  if (!raw) return "Something went wrong. Please try again.";
  if (/anonymous sign-?ins? (are |is )?disabled/i.test(raw)) {
    return "Guest mode isn't switched on yet — sign in with Discord or Spotify instead.";
  }
  if (/manual linking (is )?disabled/i.test(raw)) {
    return "Claiming isn't switched on yet — your guest profile still works; try again later.";
  }
  return raw;
}
